import { logger } from '@adornis/base/logging.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { defer, Observable, switchMap } from 'rxjs';
import { loadAndAssert } from '../authz/access.js';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { hasWorkflowPendingTasks } from './pending-tasks/pending-task.js';
import { runWorkflowAgentPass } from './pending-tasks/workflow-agent.js';

export const getConversationByIdQuery = registerQuery({
  operationName: 'getConversationByIdQuery',
  type: () => Conversation,
  params: [{ name: 'conversationId', type: () => String }],

  resolve:
    (conversationId: string) =>
    async (_gqlFields: BaseQLSelectionSet<Conversation>): Promise<Conversation | null> => {
      await loadAndAssert(Conversation, conversationId, 'read', { _id: 1 });
      const conversationCollection = await getCollection(Conversation);
      const conversation = await conversationCollection.findOne({ _id: conversationId });
      return conversation as Conversation | null;
    },
});

/**
 * Standing subscription that drives AND delivers server-initiated workflow replies (e.g. after
 * background grading finishes), with no in-memory cross-instance state.
 *
 * The resolver runs on the same instance as the client's socket, so it tails the exam change stream
 * and — when a workflow-priority pending task appears — runs the agent pass *right here*, streaming
 * previews straight to `subscriber.next`. That's co-located streaming with zero per-chunk DB writes,
 * exactly like generateAnswer. The atomic thinking-lock inside runWorkflowAgentPass dedupes against
 * user turns and other tabs/instances (only one runs; the rest get the final via the conversation
 * change stream). When no client is subscribed, pushWorkflowTask's scheduled fallback writes the
 * final message instead.
 */
export const streamConversationWorkflowSubscription = registerSubscription({
  operationName: 'streamConversationWorkflowSubscription',
  type: () => Conversation,
  params: [{ name: 'conversationId', type: () => String }],

  resolve: (conversationId: string) => (_gqlFields: BaseQLSelectionSet<Conversation>) =>
    new Observable<Conversation>(subscriber => {
      let cleanup: (() => void) | null = null;
      let cancelled = false;
      let running = false;

      void (async () => {
        // Runs agent passes against the linked exam on this caller's behalf — own conversation only.
        try {
          await loadAndAssert(Conversation, conversationId, 'read', { _id: 1 });
        } catch (err) {
          subscriber.error(err);
          return;
        }
        const conversationCollection = await getCollection(Conversation);
        const conv = await conversationCollection.findOne({ _id: conversationId });
        if (cancelled) return;

        const examId = String((conv as any)?.examId ?? '').trim();
        if (!examId) return; // no exam linked yet — nothing to watch

        const sub = watchQuery(Exam._collectionName, { _id: examId }, { limit: 1 }).subscribe({
          next: (rows: any) => {
            const exam = Array.isArray(rows) ? rows[0] : undefined;
            const pendingTasks = (exam as any)?.examState?.pendingTasks;
            if (!hasWorkflowPendingTasks(Array.isArray(pendingTasks) ? pendingTasks : [])) return;
            if (running) return; // a pass from this resolver is already in flight

            running = true;
            void runWorkflowAgentPass(conversationId, examId, c => subscriber.next(c))
              .catch(err => logger.error({ err, conversationId, context: 'WorkflowSub' }, 'pass failed for conversation'))
              .finally(() => {
                running = false;
              });
          },
          error: err => subscriber.error(err),
          complete: () => subscriber.complete(),
        });
        cleanup = () => sub.unsubscribe();
      })();

      return () => {
        cancelled = true;
        cleanup?.();
      };
    }),
});

export const streamConversationByIdSubscription = registerSubscription({
  operationName: 'streamConversationByIdSubscription',
  type: () => Conversation,
  params: [{ name: 'conversationId', type: () => String }],

  resolve: (conversationId: string) => (_gqlFields: BaseQLSelectionSet<Conversation>) =>
    new Observable<Conversation>(subscriber => {
      const sub = watchQuery(Conversation._collectionName, { _id: conversationId }, { limit: 1 }).subscribe({
        next: rows => {
          const conversation = Array.isArray(rows) ? (rows[0] as Conversation | undefined) : undefined;
          if (conversation) subscriber.next(conversation);
        },
        error: err => subscriber.error(err),
        complete: () => subscriber.complete(),
      });
      return () => sub.unsubscribe();
      // watchQuery is unfiltered: assert read on the conversation once, on subscribe, before watching it.
    }).pipe(source =>
      defer(() => loadAndAssert(Conversation, conversationId, 'read', { _id: 1 })).pipe(switchMap(() => source)),
    ),
});

/**
 * Returns the exam IDs of the current user's conversations that currently have an unread agent
 * reply. The sidebar uses this to seed its badge set on mount, so badges survive refresh / device
 * switches without any client-side persistence.
 */
export const getUnreadExamIdsQuery = registerQuery({
  operationName: 'getUnreadExamIdsQuery',
  type: () => [String],
  params: [],

  resolve: () => async (): Promise<string[]> => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    logger.info({
      hasUser: Boolean(currentUser?._id),
      userId: currentUser?._id,
    }, 'getUnreadExamIdsQuery: loading unread exam ids');
    if (!currentUser?._id) return [];

    const conversationCollection = await getCollection(Conversation);
    const docs = await conversationCollection
      .find({ userID: currentUser._id, hasUnreadReply: true } as any)
      .project({ examId: 1 })
      .toArray();

    return docs.map((d: any) => String(d?.examId || '')).filter(Boolean);
  },
});

/**
 * Clears the `hasUnreadReply` flag on a conversation (the user has now seen the latest agent
 * message). Authorized against `userID === currentUser._id`.
 */
export const markConversationAsReadMutation = registerMutation({
  operationName: 'markConversationAsRead',
  type: () => Boolean,
  params: [{ name: 'conversationId', type: () => String }],

  resolve: (conversationId: string) => async (): Promise<boolean> => {
    const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
    logger.info({
      conversationId,
      userId: currentUser?._id,
      hasUser: Boolean(currentUser?._id),
    }, 'markConversationAsRead: requested');
    if (!currentUser?._id) return false;

    const collection = await getRawCollection(Conversation._collectionName);
    const result = await collection.updateOne({ _id: conversationId, userID: currentUser._id } as any, {
      $set: { hasUnreadReply: false },
    });
    return result.matchedCount > 0;
  },
});

/**
 * Single per-user stream that emits a JSON-encoded `{ conversationId, examId }` whenever the
 * `hasUnreadReply` flag on one of the user's conversations transitions from false → true (i.e.,
 * the global change-stream watcher in `server/conversation-unread-watcher.ts` just flagged a
 * fresh agent reply).
 *
 * Drives the toaster + sidebar badge live updates. The user-vs-agent distinction is already
 * done server-side by the watcher, so we don't ship message arrays over the wire.
 */
export const streamUserConversationActivitySubscription = registerSubscription({
  operationName: 'streamUserConversationActivitySubscription',
  type: () => String,
  params: [],

  resolve: () => () =>
    new Observable<string>(subscriber => {
      let cleanup: (() => void) | null = null;
      let cancelled = false;

      void (async () => {
        const currentUser = await CurrentUserInfo.getMyself()({ _id: 1 });
        if (cancelled) return;
        // Logged-out / guest users share a global `userID: null` bucket; skip the watcher
        // entirely rather than leak activity across guest sessions.
        if (!currentUser?._id) return;

        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const filter = { userID: currentUser._id, lastUpdated: { $gte: oneHourAgo } };
        const lastFlag = new Map<string, boolean>();
        let initialEmissionDone = false;

        const sub = watchQuery(Conversation._collectionName, filter, {
          projection: { _id: 1, examId: 1, hasUnreadReply: 1 },
        }).subscribe({
          next: (rows: any) => {
            if (!Array.isArray(rows)) return;
            const wasInitial = !initialEmissionDone;
            initialEmissionDone = true;

            for (const conv of rows) {
              const conversationId = String(conv?._id || '');
              if (!conversationId) continue;
              const current = conv?.hasUnreadReply === true;
              const previous = lastFlag.get(conversationId);
              lastFlag.set(conversationId, current);

              // Initial fetch: baseline silently — existing unread state is seeded separately
              // by `getUnreadExamIdsQuery` on the client.
              if (wasInitial) continue;

              // Post-initial first observation of this conversation: emit if the flag is
              // already true. Catches the race where a brand-new conversation gets its insert
              // event AND its watcher-flipped update event delivered together — without this
              // branch we'd silently baseline `true` and never notify.
              if (previous === undefined) {
                if (current) {
                  subscriber.next(JSON.stringify({ conversationId, examId: String(conv?.examId || '') }));
                }
                continue;
              }

              if (!previous && current) {
                logger.info({
                  conversationId,
                  examId: String(conv?.examId || ''),
                }, 'streamUserConversationActivitySubscription: emitting unread event on flag transition');
                subscriber.next(JSON.stringify({ conversationId, examId: String(conv?.examId || '') }));
              }
            }
          },
          error: err => subscriber.error(err),
          complete: () => subscriber.complete(),
        });
        cleanup = () => sub.unsubscribe();
      })();

      return () => {
        cancelled = true;
        cleanup?.();
      };
    }),
});
